import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import Link from '@atlaskit/link';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import React, { useState } from 'react';

import { useMessage, useMessageString } from '@canvas/intl';
import { CanvasLocalStorageKey, safeGet, safeSet } from '@canvas/local-storage';

import { messages } from './i18n';

const SLACK_CHANNEL_URL = 'https://atlassian.enterprise.slack.com/archives/C0BPV13RZUN';

const DISMISSED_VALUE = 'true';

const bannerStyles = xcss({
  /*
   * `discovery` is the purple new-feature/changeboarding colour used elsewhere in
   * Canvas (see `--ds-background-discovery` in shapes' shape-changeboarding.module.css),
   * rather than the blue `information` used for operational notices.
   */
  backgroundColor: 'color.background.discovery',
  paddingBlock: 'space.100',
  paddingInline: 'space.150',
  /*
   * The Whiteboards app root sets `pointer-events: none` so clicks fall through to
   * the WebGL canvas. An interactive banner has to opt itself back in rather than
   * trust the host wrapper to do it — otherwise the link and dismiss button are
   * invisible to hit-testing and every click lands on the board underneath.
   * A `none` ancestor does not stop a descendant re-enabling this.
   */
  pointerEvents: 'auto',
});

const messageStyles = xcss({
  /*
   * Lets long copy wrap *inside* this column. Without it the flex item refuses to
   * shrink below its content width and the dismiss control gets pushed away.
   */
  minWidth: '0',
});

const dismissStyles = xcss({
  /* Hold the dismiss control at its intrinsic size, top-right, however the copy wraps. */
  flexShrink: 0,
});

interface NcsAnnouncementBannerProps {
  /**
   * Host-supplied positioning/elevation. Whiteboards overlays the WebGL canvas and
   * must place the banner on an explicit layer; Databases renders it in normal flow
   * and passes nothing.
   */
  className?: string;
}

/**
 * Product-wide heads-up about the NCS refactor, shared across Whiteboards, Slides,
 * and Databases. Renders nothing once the user has dismissed it.
 *
 * Rollout gating is the caller's job: each entry point wraps this in an
 * `isFeatureFlagEnabled('ncsRefactorAnnouncementBanner')` check so the flag is
 * visible at the mount site rather than buried in a presentational component.
 */
export const NcsAnnouncementBanner: React.FC<NcsAnnouncementBannerProps> =
  function NcsAnnouncementBanner({ className }: { className?: string }) {
    const [dismissed, setDismissed] = useState(
      () =>
        safeGet(CanvasLocalStorageKey.NCS_REFACTOR_ANNOUNCEMENT_DISMISSED) ===
        DISMISSED_VALUE,
    );

    const dismissLabel = useMessageString({
      message: messages['whiteboards.ncs-announcement-banner.dismiss.non-final'],
    });
    const bannerContent = useMessage({
      message: messages['whiteboards.ncs-announcement-banner.body.non-final'],
      values: {
        link: (chunks: React.ReactNode) => (
          <Link href={SLACK_CHANNEL_URL} target="_blank" rel="noopener noreferrer">
            {chunks}
          </Link>
        ),
      },
    });

    if (dismissed) {
      return null;
    }

    const onDismiss = () => {
      safeSet(CanvasLocalStorageKey.NCS_REFACTOR_ANNOUNCEMENT_DISMISSED, DISMISSED_VALUE);
      setDismissed(true);
    };

    /*
     * `data-canvas-target` below is load-bearing, not decorative. Whiteboards
     * attaches InputCapture to document.body and classifies every pointer event by
     * `closest('[data-canvas-target]')`. `<body>` itself carries
     * `data-canvas-target="board"`, so without this attribute a press on the banner
     * resolves to 'board': the input machine captures the pointer on `<body>` and
     * treats the click as a board interaction, leaving the link and dismiss button
     * dead. The value is registered in `canvasEventTargets` and `uiTargets`.
     */
    return (
      <div
        className={className}
        data-canvas-target="ncs-announcement-banner"
        data-testid="ncs-announcement-banner-root"
      >
        {/*
          Deliberately not `shouldWrap`: wrapping the row drops the dismiss
          control onto its own line, where `space-between` strands it away
          from the copy. Instead the message column shrinks and its text
          wraps, keeping the control anchored top-right at every width.
        */}
        <Inline
          alignBlock="start"
          space="space.100"
          spread="space-between"
          xcss={bannerStyles}
          testId="ncs-announcement-banner"
        >
          <Inline
            alignBlock="start"
            grow="fill"
            space="space.100"
            xcss={messageStyles}
            testId="ncs-announcement-banner-message"
          >
            <MegaphoneIcon label="" spacing="spacious" />
            <Text>{bannerContent}</Text>
          </Inline>
          <Box xcss={dismissStyles}>
            <IconButton
              appearance="subtle"
              icon={(iconProps) => <CrossIcon {...iconProps} size="medium" />}
              label={dismissLabel}
              onClick={onDismiss}
              spacing="compact"
              testId="ncs-announcement-banner-dismiss"
            />
          </Box>
        </Inline>
      </div>
    );
  };
